// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IUser } from '@/types';

interface GetUserArgs {
  params?: Record<string, string | number>;
}

const addUser = createAsyncThunk('adminUser/addUser', async (userData: IUser, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/admin/users', userData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding user failed');
  }
});

const getAllUsers = createAsyncThunk('adminUser/getUsers', async ({ params = {} }: GetUserArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/admin/users${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving users failed');
  }
});

const getUser = createAsyncThunk('adminUser/getUser', async (userIdOrSlug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/admin/users/${userIdOrSlug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single user failed');
  }
});

const updateUser = createAsyncThunk('adminUser/updateUser', async (params: { userIdOrSlug: string; updatedData: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/admin/users/${params.userIdOrSlug}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating user failed');
  }
});

const deleteUser = createAsyncThunk('adminUser/deleteUser', async (userIdOrSlug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/admin/users/${userIdOrSlug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting user failed');
  }
});

const adminUserSlice = createSlice({
  name: 'adminUser',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // addUser
      .addCase(addUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // getAllUsers
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getUser
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateUser
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteUser
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = adminUserSlice.actions;
export { addUser, deleteUser, getAllUsers, getUser, updateUser };
export default adminUserSlice.reducer;
